// ============================================================================
// RISK DETECTION ENGINE
// Evaluates finance documents for fraud and risk indicators including:
// - New contact invoicing (especially with inventory dispatch)
// - Recent delivery address changes
// - Recent payment term changes (e.g. COD → 30 days)
// - Outstanding contact documentation
// - High-value first transactions
// ============================================================================

export type RiskSeverity = 'low' | 'medium' | 'high' | 'critical';

export type RiskCategory =
  | 'new_contact_invoice'
  | 'delivery_address_change'
  | 'payment_term_change'
  | 'outstanding_documentation'
  | 'high_value_first_transaction'
  | 'inactive_contact'
  | 'missing_verification';

export interface RiskFlag {
  id: string;
  category: RiskCategory;
  severity: RiskSeverity;
  title: string;
  detail: string;
  recommendation: string;
  requiresAcknowledgement: boolean;
}

export interface RiskAssessmentInput {
  // Contact info
  contactId?: number | null;
  contactName?: string | null;
  contactCreatedAt?: string | null;
  contactUpdatedAt?: string | null;
  contactLastInvoiceDate?: string | null;
  contactInvoiceCount?: number;
  contactIsVerified?: boolean | null;
  contactIsActive?: boolean | null;
  // Payment terms
  currentPaymentTermsDays?: number | null;
  previousPaymentTermsDays?: number | null;
  paymentTermsChangedAt?: string | null;
  // Delivery address
  deliveryAddressUpdatedAt?: string | null;
  deliveryAddressChangedRecently?: boolean;
  // Documentation
  outstandingDocuments?: Array<{
    document_name: string;
    document_type?: string | null;
    is_expired?: boolean;
    is_verified?: boolean;
  }>;
  // Document/transaction context
  documentType?: string;
  documentGrandTotal?: number | null;
  hasInventoryItems?: boolean;
  warehouseName?: string | null;
  // Whether this is a sales document (vs purchase)
  isSalesDocument?: boolean;
}

export interface RiskAssessmentResult {
  flags: RiskFlag[];
  overallSeverity: RiskSeverity;
  hasBlockingRisk: boolean;
  hasWarningRisk: boolean;
  summary: string;
}

const SEVERITY_ORDER: Record<RiskSeverity, number> = {
  low: 1,
  medium: 2,
  high: 3,
  critical: 4,
};

const RECENT_CHANGE_THRESHOLD_HOURS = 72; // 3 days
const NEW_CONTACT_THRESHOLD_DAYS = 30;
const HIGH_VALUE_THRESHOLD = 50000; // R50,000

function hoursSince(dateStr: string | null | undefined): number | null {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return null;
  return (Date.now() - date.getTime()) / (1000 * 60 * 60);
}

function daysSince(dateStr: string | null | undefined): number | null {
  const hrs = hoursSince(dateStr);
  return hrs === null ? null : hrs / 24;
}

function maxSeverity(flags: RiskFlag[]): RiskSeverity {
  if (flags.length === 0) return 'low';
  return flags.reduce((max, f) =>
    SEVERITY_ORDER[f.severity] > SEVERITY_ORDER[max] ? f.severity : max
  , 'low' as RiskSeverity);
}

export function assessRisk(input: RiskAssessmentInput): RiskAssessmentResult {
  const flags: RiskFlag[] = [];

  // 1. New contact invoicing — especially with inventory dispatch
  if (input.isSalesDocument && input.contactId) {
    const createdHours = hoursSince(input.contactCreatedAt);
    const invoiceCount = input.contactInvoiceCount ?? 0;

    if (createdHours !== null && createdHours < NEW_CONTACT_THRESHOLD_DAYS * 24 && invoiceCount === 0) {
      const severity: RiskSeverity = input.hasInventoryItems ? 'high' : 'medium';
      flags.push({
        id: 'new_contact_invoice',
        category: 'new_contact_invoice',
        severity,
        title: 'New contact being invoiced',
        detail: `Contact "${input.contactName || 'Unknown'}" was created ${Math.round(createdHours / 24)} days ago and has no prior invoices.${input.hasInventoryItems ? ' Inventory items are being dispatched — goods could be shipped before payment is confirmed.' : ''}`,
        recommendation: input.hasInventoryItems
          ? 'Consider requiring payment upfront (COD) or a deposit before dispatching inventory to a new contact.'
          : 'Verify contact details and consider shorter payment terms for the first transaction.',
        requiresAcknowledgement: (severity as RiskSeverity) === 'high' || (severity as RiskSeverity) === 'critical',
      });
    }
  }

  // 2. High-value first transaction
  if (input.isSalesDocument && input.contactId && (input.contactInvoiceCount ?? 0) === 0) {
    const total = input.documentGrandTotal ?? 0;
    if (total >= HIGH_VALUE_THRESHOLD) {
      flags.push({
        id: 'high_value_first_transaction',
        category: 'high_value_first_transaction',
        severity: 'high',
        title: 'High-value first transaction',
        detail: `This is the first invoice for "${input.contactName || 'this contact'}" and the total (R${total.toLocaleString()}) exceeds the high-value threshold of R${HIGH_VALUE_THRESHOLD.toLocaleString()}.`,
        recommendation: 'Request a credit check or references before extending credit on this transaction.',
        requiresAcknowledgement: true,
      });
    }
  }

  // 3. Recent delivery address change
  if (input.deliveryAddressChangedRecently || (input.deliveryAddressUpdatedAt && (hoursSince(input.deliveryAddressUpdatedAt) ?? Infinity) < RECENT_CHANGE_THRESHOLD_HOURS)) {
    const changedHours = hoursSince(input.deliveryAddressUpdatedAt);
    flags.push({
      id: 'delivery_address_change',
      category: 'delivery_address_change',
      severity: input.hasInventoryItems ? 'high' : 'medium',
      title: 'Delivery address recently changed',
      detail: `The delivery address for "${input.contactName || 'this contact'}" was changed ${changedHours !== null ? Math.round(changedHours) + ' hours' : 'recently'} ago.${input.hasInventoryItems ? ' Inventory is being dispatched to this recently changed address — verify the address before shipping.' : ''}`,
      recommendation: 'Confirm the new delivery address with the contact via phone or email before dispatching goods.',
      requiresAcknowledgement: Boolean(input.hasInventoryItems),
    });
  }

  // 4. Recent payment term change (especially COD → terms)
  if (input.previousPaymentTermsDays !== null && input.currentPaymentTermsDays !== null &&
      input.previousPaymentTermsDays !== undefined && input.currentPaymentTermsDays !== undefined &&
      input.previousPaymentTermsDays !== input.currentPaymentTermsDays) {
    const changedHours = hoursSince(input.paymentTermsChangedAt);
    const isRecent = changedHours !== null && changedHours < RECENT_CHANGE_THRESHOLD_HOURS;
    const termsExtended = (input.currentPaymentTermsDays ?? 0) > (input.previousPaymentTermsDays ?? 0);

    if (isRecent && termsExtended) {
      const fromLabel = input.previousPaymentTermsDays === 0 ? 'COD' : `${input.previousPaymentTermsDays} days`;
      const toLabel = input.currentPaymentTermsDays === 0 ? 'COD' : `${input.currentPaymentTermsDays} days`;
      flags.push({
        id: 'payment_term_change',
        category: 'payment_term_change',
        severity: 'medium',
        title: 'Payment terms recently extended',
        detail: `Payment terms for "${input.contactName || 'this contact'}" were recently changed from ${fromLabel} to ${toLabel}.`,
        recommendation: 'Confirm that the payment term change was authorised and that the contact creditworthiness supports the new terms.',
        requiresAcknowledgement: false,
      });
    }
  }

  // 5. Outstanding documentation
  const outstanding = input.outstandingDocuments ?? [];
  const expiredDocs = outstanding.filter((d) => d.is_expired);
  const unverifiedDocs = outstanding.filter((d) => !d.is_verified && !d.is_expired);

  if (expiredDocs.length > 0) {
    flags.push({
      id: 'expired_documentation',
      category: 'outstanding_documentation',
      severity: 'high',
      title: 'Expired contact documentation',
      detail: `"${input.contactName || 'This contact'}" has ${expiredDocs.length} expired document(s): ${expiredDocs.map((d) => d.document_name).join(', ')}.`,
      recommendation: 'Request updated documents before proceeding with credit terms or large transactions.',
      requiresAcknowledgement: true,
    });
  }

  if (unverifiedDocs.length > 0) {
    flags.push({
      id: 'unverified_documentation',
      category: 'outstanding_documentation',
      severity: 'medium',
      title: 'Unverified contact documentation',
      detail: `"${input.contactName || 'This contact'}" has ${unverifiedDocs.length} unverified document(s): ${unverifiedDocs.map((d) => d.document_name).join(', ')}.`,
      recommendation: 'Verify the submitted documents before extending credit terms.',
      requiresAcknowledgement: false,
    });
  }

  // 6. Inactive contact being invoiced
  if (input.contactIsActive === false && input.isSalesDocument) {
    flags.push({
      id: 'inactive_contact',
      category: 'inactive_contact',
      severity: 'medium',
      title: 'Inactive contact being invoiced',
      detail: `"${input.contactName || 'This contact'}" is marked as inactive. Invoicing an inactive contact may indicate unauthorised activity.`,
      recommendation: 'Verify that this contact should still be transacting and reactivate if appropriate.',
      requiresAcknowledgement: false,
    });
  }

  // 7. Missing verification for new contact with credit terms
  if (input.isSalesDocument && input.contactIsVerified === false && (input.currentPaymentTermsDays ?? 0) > 0) {
    flags.push({
      id: 'missing_verification',
      category: 'missing_verification',
      severity: 'medium',
      title: 'Unverified contact with credit terms',
      detail: `"${input.contactName || 'This contact'}" is not verified but has payment terms of ${input.currentPaymentTermsDays} days. Credit is being extended without verification.`,
      recommendation: 'Complete contact verification (KYC, references, credit check) before extending credit terms.',
      requiresAcknowledgement: false,
    });
  }

  const overall = maxSeverity(flags);
  const hasBlocking = flags.some((f) => f.requiresAcknowledgement && (f.severity === 'high' || f.severity === 'critical'));
  const hasWarning = flags.some((f) => (f.severity as RiskSeverity) === 'medium');

  const summary = flags.length === 0
    ? 'No risk indicators detected.'
    : `${flags.length} risk indicator${flags.length === 1 ? '' : 's'} detected (${flags.filter((f) => f.severity === 'high' || f.severity === 'critical').length} high severity).`;

  return {
    flags,
    overallSeverity: overall,
    hasBlockingRisk: hasBlocking,
    hasWarningRisk: hasWarning,
    summary,
  };
}

export function formatRiskSummary(flags: RiskFlag[]): string {
  if (flags.length === 0) return '';
  const high = flags.filter((f) => f.severity === 'high' || f.severity === 'critical');
  const medium = flags.filter((f) => f.severity === 'medium');
  const parts: string[] = [];
  if (high.length > 0) parts.push(`${high.length} high-risk`);
  if (medium.length > 0) parts.push(`${medium.length} medium-risk`);
  return parts.join(', ');
}
